import React, { useEffect, useMemo, useState } from 'react'
import { Building2 } from 'lucide-react'
import { Button } from '@headlessui/react'
import { BriefcaseIcon, BuildingIcon } from 'assets/Icons'
import toast from 'react-hot-toast'
import SearchCreateSelect from 'components/common/SearchCreateSelect'
import CompaniesModal from 'components/Modals/CompaniesModal'
import CreateDealModal from 'components/Modals/CreateDealModal'
import { CompanyFormValues } from 'types/crm/company'
import { Company } from 'types/crm/company'
import {
  useContactCompanies,
  useContactDeals,
  useCreateContactCompanyMutation,
  useCreateDealMutation,
  useLinkCompanyMutation,
  useNonAssociatedCompanies,
} from 'service/crm'
import { DealFormValues, DealAssociations } from 'types/crm/deal'
import { Contact } from 'types/crm/contacts'
import StatCard, { Stat } from 'components/common/StatCard'
import { useCrmNavigation } from 'hooks/useCrmNavigation'
import SectionHeader from 'components/common/SectionHeader'
import AssociationEmptyState from '../AssociationEmptyState'
import CompanyRow from '../CompanyRow'
import DealCard, { Deal } from '../Deals/DealCard'
import Timestamps from '../Timestamps'
import { useRecordAccess } from 'context/crm/RecordAccessContext'
import { formatDealAmount } from 'utils/helpers'


export type Timestamp = {
  label: string
  value: string
}


type ContactAssociationProps = {
  contactUuid: string
  /**
   * Real figures, computed by the detail page from the contact payload.
   * Nothing is rendered when empty — no placeholder/mock values.
   */
  stats?: Stat[]
  timestamps?: Timestamp[]
  onAddCompany?: () => void
  /** This contact's own display info, so "Create Deal" can pre-select it as a locked association. */
  contact?: Pick<Contact, 'fullName' | 'jobTitle' | 'companyName' | 'email' | 'profileImageUrl'>
}


const ContactAssociation = ({
  contactUuid,
  stats = [],
  timestamps = [],
  onAddCompany,
  contact,
}: ContactAssociationProps) => {
  const [showCompanySearch, setShowCompanySearch] = useState(false)
  const [companySearchTerm, setCompanySearchTerm] = useState('')
  const [showAllCompanies, setShowAllCompanies] = useState(false)
  const [showAllDeals, setShowAllDeals] = useState(false)
  const [isCreateDealModalOpen, setIsCreateDealModalOpen] = useState(false)

  /**
   * RBAC (sections 6, 13): "Add Deal" and "Add Company / Association" are
   * modification actions on THIS contact, so they ride on this contact's access —
   * not on the associated company's or deal's own canEdit. Viewing the lists,
   * expanding "View all", and clicking through to a deal are view actions and
   * stay available to everyone (sections 4, 14).
   */
  const { canManageAssociations } = useRecordAccess()

  const { toViewDeal } = useCrmNavigation()

  const { data: dealsData = [] } = useContactDeals(contactUuid)
  const createDealMutation = useCreateDealMutation()

  const deals: Deal[] = useMemo(
    () =>
      dealsData.map((deal) => ({
        id: deal.uuid,
        dealKey: deal.dealKey,
        name: deal.name,
        amount:
          deal.amount != null
            ? formatDealAmount(deal.amount, deal.currency || 'INR')
            : '—',
        stage: deal.dealStageLabel,
        closeDate: deal.closeDate ?? '',
      })),
    [dealsData],
  )

  const { data: companyList = [] } = useContactCompanies(contactUuid)
  const createCompanyMutation = useCreateContactCompanyMutation()
  const linkCompanyMutation = useLinkCompanyMutation()

  const {
    companies: nonAssociatedCompanies,
    isLoading: isSearchingCompanies,
    isFetchingMore: isFetchingMoreCompanies,
    hasMore: hasMoreCompanies,
    onLoadMore: loadMoreCompanies,
  } = useNonAssociatedCompanies(
    contactUuid,
    { search: companySearchTerm.trim() || undefined, limit: 20 },
    // Never run the non-associated lookup for a view-only user — it exists only
    // to feed the link-company picker they can't open.
    { enabled: showCompanySearch && canManageAssociations },
  )

  const companyOptions = useMemo(
    () =>
      nonAssociatedCompanies.map((c: Company) => ({
        uuid: c.uuid,
        name: c.name,
        subtitle: [c.industry, c.cityLocation].filter(Boolean).join(' · '),
        imageUrl: c.imageUrl || null,
      })),
    [nonAssociatedCompanies],
  )

  // Section 23: access can disappear mid-session. Collapse anything open.
  useEffect(() => {
    if (!canManageAssociations) {
      setShowCompanySearch(false)
      setCompanySearchTerm('')
      setIsCreateDealModalOpen(false)
    }
  }, [canManageAssociations])

  const handleViewDeal = (deal: Deal) => {
    if (!deal.dealKey) {
      toast.error(`Can't open ${deal.name} — this deal has no reference key.`)
      return
    }
    toViewDeal(deal.dealKey)
  }

  const handleLinkCompany = (companyUuid: string) => {
    if (!canManageAssociations) {
      toast.error('You do not have permission to change associations on this contact.')
      return
    }
    linkCompanyMutation.mutate(
      { contactUuid, companyUuid },
      {
        onSuccess: () => toast.success('Company added to contact'),
        onError: (err: any) =>
          toast.error(
            err?.response?.data?.message ??
            err?.message ??
            'Failed to add company. Please try again.',
          ),
      },
    )
  }

  const handleCreateCompany = async (values: CompanyFormValues) => {
    if (!canManageAssociations) {
      throw new Error('You do not have permission to change associations on this contact.')
    }
    await createCompanyMutation.mutateAsync({
      contactUuid,
      companyName: values.title,
      industry: values.industry || undefined,
      cityLocation: values.city || undefined,
      websiteUrl: values.website || undefined,
      phone: values.phone || undefined,
      companyOwnerUuid: values.companyOwnerUuid || undefined,
      linkedInUrl: values.linkedin || undefined,
      tags: values.tags,
      description: values.description || undefined,
      timezone: Intl.DateTimeFormat().resolvedOptions().timeZone,
    })
  }

  const handleToggleCompanySearch = () => {
    if (!canManageAssociations) return
    setShowCompanySearch((prev) => !prev)
    setCompanySearchTerm('')
    onAddCompany?.()
  }

  const handleOpenCreateDeal = () => {
    if (!canManageAssociations) return
    setIsCreateDealModalOpen(true)
  }

  const handleCreateDeal = async (values: DealFormValues, associations: DealAssociations) => {
    if (!canManageAssociations) {
      toast.error('You do not have permission to add deals to this contact.')
      setIsCreateDealModalOpen(false)
      return
    }
    try {
      await createDealMutation.mutateAsync({
        contactUuid,
        dealName: values.name,
        pipeline: values.pipeline || undefined,
        dealStage: values.dealStage || undefined,
        amount: values.amount !== '' ? Number(values.amount) : undefined,
        currency: values.currency || undefined,
        closeDate: values.closeDate || undefined,
        dealOwnerUuid: values.owner || undefined,
        dealType: values.dealType || undefined,
        priority: values.priority || undefined,
        associatedContactUuid: contactUuid,
        associatedContactReferences: associations.contactUuids.length ? associations.contactUuids : undefined,
        associatedCompanyReferences: associations.companyUuids.length ? associations.companyUuids : undefined,
        description: values.description || undefined,
        timezone: Intl.DateTimeFormat().resolvedOptions().timeZone,
      })
      toast.success('Deal created successfully')
      setIsCreateDealModalOpen(false)
    } catch (err: any) {
      toast.error(
        err?.response?.data?.message ??
        err?.message ??
        'Failed to create deal. Please try again.',
      )
    }
  }

  const visibleCompanies = showAllCompanies ? companyList : companyList.slice(0, 3)
  const visibleDeals = showAllDeals ? deals : deals.slice(0, 3)

  return (
    <>
      <div className="w-full min-h-full rounded-r-xl bg-primary border-l border-primary-subtle">
        {/* Stats — only rendered when the page supplies real figures. */}
        {stats.length > 0 && (
          <div className="grid grid-cols-2 gap-3 p-4 border-b border-primary-subtle">
            {stats.map((stat) => (
              <StatCard key={stat.label} {...stat} />
            ))}
          </div>
        )}

        <div className='p-4 border-b border-primary-subtle'>
          <SectionHeader
            title="OPEN DEALS"
            onAdd={canManageAssociations ? handleOpenCreateDeal : undefined}
          />
          {deals.length === 0 ? (
            <AssociationEmptyState
              icon={<BriefcaseIcon size={20} />}
              title="No open deals"
              description="Link a deal to track revenue tied to this contact."
              actionLabel={canManageAssociations ? 'Add deal' : undefined}
              onAction={canManageAssociations ? handleOpenCreateDeal : undefined}
            />
          ) : (
            <div
              className={`space-y-3 ${showAllDeals ? 'max-h-[530px] overflow-y-auto pr-1' : ''
                }`}
            >
              {visibleDeals.map((deal) => (
                <DealCard
                  key={deal.id}
                  deal={deal}
                  onClick={() => handleViewDeal(deal)}
                />
              ))}
            </div>
          )}
          {deals.length > 3 && (
            <Button
              onClick={() => setShowAllDeals((prev) => !prev)}
              className="mt-3 w-full rounded-xl border border-primary-subtle py-2.5 text-center text-[14px] font-medium text-neutral-faded data-active:opacity-70"
            >
              {showAllDeals ? 'Show less' : 'View all'}
            </Button>
          )}
        </div>

        <div className='p-4 border-b border-primary-subtle'>
          <SectionHeader
            title="COMPANIES"
            onAdd={canManageAssociations ? handleToggleCompanySearch : undefined}
          />
          {showCompanySearch && canManageAssociations && (
            <div className="mb-3">
              <SearchCreateSelect
                placeholder="Search to add companies..."
                listHeading="Existing Companies"
                emptyLabel="No companies found"
                createButtonLabel="Create New Company"
                icon={Building2}
                value={companySearchTerm}
                options={companyOptions}
                isLoading={isSearchingCompanies}
                isFetchingMore={isFetchingMoreCompanies}
                hasMore={hasMoreCompanies}
                onLoadMore={loadMoreCompanies}
                createDisplay="modal"
                showOptionAddAction
                clearOnSelect
                onSearchChange={setCompanySearchTerm}
                onSelectOption={(option) => {
                  handleLinkCompany(option.uuid)
                  setCompanySearchTerm('')
                }}
                renderCreateModal={({ open, onClose }) => (
                  <CompaniesModal
                    isOpen={open}
                    onClose={onClose}
                    isSaving={createCompanyMutation.isPending}
                    onSubmit={async (values) => {
                      try {
                        await handleCreateCompany(values)
                        toast.success('Company created successfully')
                        onClose()
                      } catch (err: any) {
                        toast.error(
                          err?.response?.data?.message ??
                          err?.message ??
                          'Failed to create company. Please try again.',
                        )
                      }
                    }}
                  />
                )}
              />
            </div>
          )}
          <div
            className={
              showAllCompanies ? 'max-h-80 overflow-y-auto pr-1' : undefined
            }
          >
            {/* CompanyRow is read-only today. If an unlink/remove action is added,
                gate it on canManageAssociations (it changes THIS contact), not on
                the company's own canEdit. */}
            {visibleCompanies.map((company) => (
              <CompanyRow key={company.uuid} company={company} />
            ))}
            {companyList.length === 0 && (
              <AssociationEmptyState
                icon={<BuildingIcon size={20} />}
                title="No company linked"
                description="Associate a company to add shared context."
                actionLabel={canManageAssociations ? 'Add company' : undefined}
                onAction={canManageAssociations ? handleToggleCompanySearch : undefined}
              />
            )}
          </div>
          {companyList.length > 3 && (
            <Button
              onClick={() => setShowAllCompanies((prev) => !prev)}
              className="mt-3 w-full rounded-xl border border-primary-subtle py-2.5 text-center text-[14px] font-medium text-neutral-faded data-active:opacity-70"
            >
              {showAllCompanies ? 'Show less' : 'View all'}
            </Button>
          )}
        </div>


        {/* Static timestamps */}
        {
          timestamps.length > 0 &&
          <Timestamps timestamps={timestamps} />
        }
      </div>

      {canManageAssociations && (
        <CreateDealModal
          isOpen={isCreateDealModalOpen}
          onClose={() => setIsCreateDealModalOpen(false)}
          onSubmit={handleCreateDeal}
          isSaving={createDealMutation.isPending}
          scope={{ type: 'contact', uuid: contactUuid }}
          scopeEntity={
            contact
              ? {
                name: contact.fullName,
                subtitle: [contact.jobTitle, contact.companyName].filter(Boolean).join(' · ') || contact.email,
                imageUrl: contact.profileImageUrl,
              }
              : undefined
          }
        />
      )}
    </>
  )
}

export default ContactAssociation